import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { MasonryIdByName } from "common/materials/library/masonryIdByName";
import type { MasonryId } from "common/materials/masonryTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { colorLibNameFromPath, dmPathWithColorLibRe, dmPathWithColorRe, getColor } from "./materialLibraryUtil";

export {
  pathStringFromMaterialSrc,
  pathStringFromMaterialSrcNoColor,
  pathStringFromMaterialSrcWithUserDefinedName,
} from "./pathStringFromMaterialSrc";

export function materialSrcFromPathStringNoColor(libraryPath: string): MaterialSrc | undefined {
  const dmPathWithColorLibMatch = libraryPath.match(dmPathWithColorLibRe);
  if (dmPathWithColorLibMatch?.groups) {
    const templateName = dmPathWithColorLibMatch.groups.template;
    if (!DesignMaterialPath.hasOwnProperty(templateName) || !DesignMaterialPath[templateName].hasOwnProperty("C")) {
      console.warn("Could not load material path: " + libraryPath);
      return undefined;
    }
    if ((DesignMaterialPath[templateName].C as MaterialSrc).templateId !== undefined) {
      return { ...DesignMaterialPath[templateName].C };
    }
  }
  const src = materialSrcFromPathString(libraryPath);
  if (src) {
    src.colorId = undefined;
  }
  return src;
}

export function materialSrcFromPathString(
  libraryPath: string,
  warnIfNotFound: boolean = true,
): MaterialSrc | undefined {
  const dmPathWithColorMatch = libraryPath.match(dmPathWithColorRe);
  if (dmPathWithColorMatch?.groups) {
    const templateName = dmPathWithColorMatch.groups.template;
    if (!DesignMaterialPath.hasOwnProperty(templateName) || !DesignMaterialPath[templateName].hasOwnProperty("C")) {
      if (warnIfNotFound) {
        console.warn("Could not load material path: " + libraryPath);
      }
      return undefined;
    }
    if ((DesignMaterialPath[templateName].C as MaterialSrc).templateId !== undefined) {
      const colorLibName = colorLibNameFromPath(libraryPath);
      if (colorLibName !== undefined) {
        const colorCandidate = getColor(colorLibName, dmPathWithColorMatch.groups.color);
        if (colorCandidate !== undefined) {
          return {
            ...DesignMaterialPath[templateName].C,
            colorId: colorCandidate,
          };
        }
      }
      if (warnIfNotFound) {
        console.warn("Could not load material path: " + libraryPath);
      }
      return undefined;
    }
  }
  const pathComponents = libraryPath.split(".");
  let libNode: any = DesignMaterialPath;
  for (let i = 0; i < pathComponents.length; ++i) {
    if (!libNode.hasOwnProperty(pathComponents[i])) {
      if (warnIfNotFound) {
        console.warn("Could not load material path: " + libraryPath);
      }
      return undefined;
    }
    libNode = libNode[pathComponents[i]];
  }
  if ((libNode as MaterialSrc).templateId === undefined) {
    if (warnIfNotFound) {
      console.warn("Could not load material path: " + libraryPath);
    }
    return undefined;
  }
  return { ...libNode };
}

export function materialSrcOrMasonryIdFromName(libraryPathOrMasonryName: string): MaterialSrc | MasonryId | undefined {
  const masonryRecord = MasonryIdByName as Record<string, MasonryId>;
  return masonryRecord.hasOwnProperty(libraryPathOrMasonryName)
    ? masonryRecord[libraryPathOrMasonryName]
    : materialSrcFromPathString(libraryPathOrMasonryName);
}

export function materialTemplateIdFromName(templateName: string): number | undefined {
  if (!DesignMaterialPath.hasOwnProperty(templateName)) {
    return undefined;
  }
  if (DesignMaterialPath[templateName].Default !== undefined) {
    return DesignMaterialPath[templateName].Default.templateId;
  }
  if (DesignMaterialPath[templateName].C !== undefined) {
    return DesignMaterialPath[templateName].C.templateId;
  }
  if (DesignMaterialPath[templateName].L !== undefined) {
    function recursiveSearch(node: Record<string, unknown>): number | undefined {
      if ((node as { templateId?: number }).templateId !== undefined) {
        return (node as { templateId: number }).templateId;
      }
      const keys = Object.keys(node);
      if (keys.length === 0) {
        return undefined;
      }
      for (const key of keys) {
        const searchResult = recursiveSearch(node[key] as Record<string, unknown>);
        if (searchResult !== undefined) {
          return searchResult;
        }
      }
      return undefined;
    }
    return recursiveSearch(DesignMaterialPath[templateName].L as Record<string, unknown>);
  }
  return undefined;
}
